/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { calculateOffset } from '#utils/PRIVATE_visLayoutUtils/layoutUtils';
import { useTooltip } from '#hooks/UNSAFE_useTooltip';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { useEffect, useRef, useState } from 'preact/hooks';

type Props = {
  getTextAndBounds: (target: EventTarget | null) =>
    | {
        text?: string;
        bounds?: Dimension;
      }
    | undefined;
  isRtl: boolean;
  width: number;
  textAndBounds: {
    text?: string;
    bounds?: Dimension;
  };
};

export function useTextTruncation({ getTextAndBounds, width, isRtl, textAndBounds }: Props) {
  const [text, setText] = useState<string>();
  const bounds = useRef<Dimension>();

  const hoveredRef = useRef(false);

  useEffect(() => {
    if (textAndBounds.text) {
      bounds.current = textAndBounds.bounds;
      setText(textAndBounds.text);
    } else if (!hoveredRef.current) {
      setText(undefined);
    }
  }, [textAndBounds]);

  const { tooltipContent, tooltipProps } = useTooltip({
    text,
    position: 'top-start',
    offset: calculateOffset(isRtl, width, bounds.current)
  });

  const onPointerMove = (event: Event) => {
    const textAndBounds = getTextAndBounds(event.target);

    if (textAndBounds) {
      bounds.current = textAndBounds.bounds;
      setText(textAndBounds.text);
      hoveredRef.current = true;
    } else {
      setText(undefined);
      hoveredRef.current = false;
    }
  };

  return {
    tooltipContent,
    tooltipProps: mergeProps({ onPointerMove }, tooltipProps)
  };
}
